/**
 * App.tsx — Root application component.
 *
 * This component is the single source of truth for all shared application state.
 * It owns every piece of state that needs to survive tab switches or be shared
 * between the four clock features (Time / Alarm / Timer / Stopwatch).
 *
 * WHAT LIVES HERE:
 *   - Theme state (light/dark mode + selected color theme)
 *   - Background image state
 *   - Font scale state
 *   - Alarm list state (persisted in localStorage)
 *   - Timer state (input values + running countdown)
 *   - Stopwatch state (elapsed time + laps)
 *   - Donation toast (shown after returning from the Buy Me a Coffee page)
 *
 * WHY STATE LIVES HERE (and not in each feature component):
 *   When the user switches tabs (e.g. from Timer to Alarm), React unmounts the
 *   old tab component and mounts the new one. If timer state lived inside the
 *   Timer component, the countdown would reset every time the user left the tab.
 *   Lifting state here keeps it alive for the full session.
 *
 * THEMING MECHANISM:
 *   Two useEffects apply colors to the page:
 *   1. data-theme attribute ("light"/"dark") on <html> activates the CSS variable
 *      overrides defined in index.css for the base light/dark colors.
 *   2. The theme useEffect reads the selected ThemePalette and writes every color
 *      as an inline CSS custom property on document.documentElement (<html>),
 *      overriding the defaults. All components read these variables via CSS.
 *
 * ROUTING:
 *   React Router maps URLs to page components. The app has two route groups:
 *     /clock/:tab        → Home page (tab = "time" | "alarm" | "timer" | "stopwatch")
 *     /information/...   → Static info pages (About, Privacy, etc.)
 *   The root "/" redirects to "/clock/time" so users always land on the clock.
 */

import "./App.css";
import React, { useState, useRef, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { themes } from "./constants/themes";
import { backgrounds } from "./constants/backgrounds";
import { AlarmItem, FrequentTimer, StopwatchLap } from "./types";
import { useDynamicFavicon } from "./hooks/useDynamicFavicon";
import { useAuth } from "./hooks/useAuth";
import type { HomeProps } from "./pages/Home/Home";
import { SettingsBarProps } from "./components/SettingsBar/SettingsBar";

// Route-level code splitting — each page becomes its own chunk so, e.g., the
// clock-only visitor never downloads Calendar's PDF/holiday-data dependencies.
const Home = lazy(() => import("./pages/Home/Home"));
const Calendar = lazy(() => import("./pages/Calendar/Calendar"));
const PrivacyPolicy = lazy(() => import("./pages/information/PrivacyPolicy/privacy-policy"));
const About = lazy(() => import("./pages/information/About/About"));
const Contact = lazy(() => import("./pages/information/Contact/Contact"));
const TermsAndConditions = lazy(() => import("./pages/information/TermsAndConditions/terms-conditions"));

function App() {
  useTranslation();
  const { user, loading: authLoading, signInWithGoogle, signOut } = useAuth();

  // Donation thank-you toast — triggered by ?donated=true in the URL
  const [showDonationToast, setShowDonationToast] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('donated') === 'true';
  });

  React.useEffect(() => {
    if (!showDonationToast) return;
    // Clean the URL param without a page reload
    const url = new URL(window.location.href);
    url.searchParams.delete('donated');
    window.history.replaceState({}, '', url.toString());
    // Auto-dismiss after 6 s
    const timer = setTimeout(() => setShowDonationToast(false), 6000);
    return () => clearTimeout(timer);
  }, [showDonationToast]);

  // Alarm state
  const [alarms, setAlarms] = useState<AlarmItem[]>(() => {
    try {
      const saved = localStorage.getItem('alarms');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [alarmNewTime, setAlarmNewTime] = useState("07:00");
  const [alarmRingingId, setAlarmRingingId] = useState<number | null>(null);

  React.useEffect(() => {
    localStorage.setItem('alarms', JSON.stringify(alarms));
  }, [alarms]);

  // Frequent timers state
  const [frequentTimers, setFrequentTimers] = useState<FrequentTimer[]>(() => {
    try {
      const saved = localStorage.getItem('frequentTimers');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  React.useEffect(() => {
    localStorage.setItem('frequentTimers', JSON.stringify(frequentTimers));
  }, [frequentTimers]);

  // Timer state
  const [timerInputDays, setTimerInputDays] = useState(0);
  const [timerInputHours, setTimerInputHours] = useState(0);
  const [timerInputMinutes, setTimerInputMinutes] = useState(0);
  const [timerInputSeconds, setTimerInputSeconds] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerFinished, setTimerFinished] = useState(false);
  const timerIntervalRef = useRef(null);

  // Stopwatch state
  const [swMs, setSwMs] = useState(0);
  const [swRunning, setSwRunning] = useState(false);
  const [swLaps, setSwLaps] = useState<StopwatchLap[]>([]);
  const swIntervalRef = useRef(null);

  // Theme state
  const [theme, setTheme] = useState(
    () => localStorage.getItem("theme") || "light"
  );

  // Font Scale state
  const [fontScale, setFontScale] = useState(() => parseFloat(localStorage.getItem('fontScale') || '1'));

  React.useEffect(() => {
    document.documentElement.style.setProperty('--font-scale', fontScale.toString());
    localStorage.setItem('fontScale', fontScale.toString());
  }, [fontScale]);

  // Theme Palette state
  const [currentThemeId, setCurrentThemeId] = useState(() => {
    const saved = localStorage.getItem("themeId");
    return saved ? parseInt(saved, 10) : 0;
  });

  // Background state
  const [currentBackgroundId, setCurrentBackgroundId] = useState(() => {
    return localStorage.getItem("backgroundId") || "none";
  });

  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  React.useEffect(() => {
    localStorage.setItem("themeId", currentThemeId.toString());
    const selectedTheme = themes.find((t) => t.id === currentThemeId);
    
    if (!selectedTheme) return;

    // Determine which palette to use based on current mode
    const palette = theme === 'dark' ? selectedTheme.dark : selectedTheme.light;

    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : "0, 0, 0";
    };

    // Apply CSS variables
    const root = document.documentElement;
    root.style.setProperty("--bg-primary", palette.bgPrimary);
    root.style.setProperty("--bg-secondary", palette.bgSecondary);
    root.style.setProperty("--bg-secondary-rgb", hexToRgb(palette.bgSecondary));
    root.style.setProperty("--text-primary", palette.textPrimary);
    root.style.setProperty("--text-secondary", palette.textSecondary);
    root.style.setProperty("--accent-color", palette.accent);
    root.style.setProperty("--accent-secondary", palette.accentSecondary);
    root.style.setProperty("--accent-rgb", hexToRgb(palette.accent));
    root.style.setProperty("--border-color", palette.border);
    
    // Map secondary background to card and input backgrounds for consistency
    root.style.setProperty("--card-bg", palette.bgSecondary);
    root.style.setProperty("--input-bg", palette.bgSecondary);

  }, [currentThemeId, theme]);

  React.useEffect(() => {
    localStorage.setItem("backgroundId", currentBackgroundId);
    const selectedBg = backgrounds.find((b) => b.id === currentBackgroundId);
    
    const root = document.documentElement;
    if (selectedBg && selectedBg.url) {
      root.style.setProperty("--background-image", `url('${selectedBg.url}')`);
      root.style.setProperty("--active-opacity", "0.15"); // High transparency for active column
      root.style.setProperty("--inactive-opacity", "0.85"); // Low transparency for inactive columns
    } else {
      root.style.removeProperty("--background-image");
      root.style.setProperty("--active-opacity", "1");
      root.style.setProperty("--inactive-opacity", "1");
    }
  }, [currentBackgroundId]);

  useDynamicFavicon(currentThemeId, theme);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const settingsProps: SettingsBarProps = {
    fontScale, setFontScale,
    currentThemeId, setCurrentThemeId,
    currentBackgroundId, setCurrentBackgroundId,
    theme, toggleTheme,
    user, authLoading, signInWithGoogle, signOut,
  };

  const homeProps: HomeProps = {
    alarms, setAlarms, alarmNewTime, setAlarmNewTime, alarmRingingId, setAlarmRingingId,
    frequentTimers, setFrequentTimers,
    timerInputDays, setTimerInputDays, timerInputHours, setTimerInputHours,
    timerInputMinutes, setTimerInputMinutes, timerInputSeconds, setTimerInputSeconds,
    timerSeconds, setTimerSeconds, timerRunning, setTimerRunning,
    timerFinished, setTimerFinished, timerIntervalRef,
    swMs, setSwMs, swRunning, setSwRunning, swLaps, setSwLaps, swIntervalRef,
    settingsProps
  };

  return (
    <BrowserRouter>
      <div className="App">
        {showDonationToast && (
          <div className="donation-toast" role="status" aria-live="polite">
            <span className="donation-toast-icon" aria-hidden="true">☕</span>
            <span>Thank you so much for your support! You&apos;re amazing! 🎉</span>
            <button
              type="button"
              className="donation-toast-close"
              onClick={() => setShowDonationToast(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Navigate to="/clock/time" replace />} />
            <Route path="/clock" element={<Navigate to="/clock/time" replace />} />
            <Route path="/clock/:tab" element={<Home {...homeProps} />} />
            <Route path="/calendar" element={<Calendar settingsProps={settingsProps} user={user} signInWithGoogle={signInWithGoogle} />} />
            <Route path="/information/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/information/about" element={<About />} />
            <Route path="/information/contact" element={<Contact />} />
            <Route path="/information/terms" element={<TermsAndConditions />} />
          </Routes>
        </Suspense>
      </div>
    </BrowserRouter>
  );
}

export default App;
